import Swal from "sweetalert2";
import { ApiService } from "../shared/utils/ApiService.js";
import { AuthService } from "../shared/services/AuthService.js";
import { AppConfig } from "../shared/utils/AppConfig.js";
import { escapeHtml } from "../shared/utils/sanitize.js";

export const ProfileController = {
  open: async() => {
    const user = AuthService.getCurrentUser();
    if (!user) return;
    if (user.proveedor && user.proveedor !== "local") return ProfileController.openExterno(user);

    const { value } = await Swal.fire({
      title: "Mi perfil",
      html: `
        <div class="text-start">
          <div class="mb-3">
            <label class="form-label fw-semibold">Nombre</label>
            <input id="profile-nombre" class="form-control" value="${user.nombre || ""}">
          </div>
          <div class="mb-4">
            <label class="form-label fw-semibold">Email</label>
            <input type="email" class="form-control" value="${user.email || ""}" disabled>
            <div class="form-text">El email no se puede modificar.</div>
          </div>

          <hr class="my-3">
          <p class="fw-semibold text-muted small mb-3">CAMBIAR CONTRASEÑA <span class="fw-normal">(opcional)</span></p>

          <div class="mb-3">
            <label class="form-label">Contraseña actual</label>
            <div class="input-group">
              <input id="profile-pw-current" type="password" class="form-control" placeholder="Tu contraseña actual">
              <button type="button" class="btn btn-outline-secondary kb-pw-toggle" data-target="profile-pw-current" tabindex="-1"><i class="fas fa-eye"></i></button>
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label">Nueva contraseña</label>
            <div class="input-group">
              <input id="profile-pw-new" type="password" class="form-control" placeholder="Mínimo 6 caracteres">
              <button type="button" class="btn btn-outline-secondary kb-pw-toggle" data-target="profile-pw-new" tabindex="-1"><i class="fas fa-eye"></i></button>
            </div>
            <div id="pw-strength-bar" style="display:none" class="mt-1">
              <div class="d-flex gap-1 mb-1">
                <div id="pw-seg1" style="height:4px;border-radius:2px;flex:1;background:#e0e0e0"></div>
                <div id="pw-seg2" style="height:4px;border-radius:2px;flex:1;background:#e0e0e0"></div>
                <div id="pw-seg3" style="height:4px;border-radius:2px;flex:1;background:#e0e0e0"></div>
              </div>
              <small id="pw-strength-label" style="font-size:0.7rem"></small>
            </div>
          </div>
          <div class="mb-1">
            <label class="form-label">Confirmar nueva contraseña</label>
            <div class="input-group">
              <input id="profile-pw-confirm" type="password" class="form-control" placeholder="Repetí la nueva contraseña">
              <button type="button" class="btn btn-outline-secondary kb-pw-toggle" data-target="profile-pw-confirm" tabindex="-1"><i class="fas fa-eye"></i></button>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      returnFocus: false,
      didOpen: () => {
        // Toggle mostrar/ocultar contraseña
        document.querySelectorAll(".kb-pw-toggle").forEach(btn => {
          btn.addEventListener("click", () => {
            const input = document.getElementById(btn.dataset.target);
            const icon = btn.querySelector("i");
            const show = input.type === "password";
            input.type = show ? "text" : "password";
            icon.classList.toggle("fa-eye", !show);
            icon.classList.toggle("fa-eye-slash", show);
          });
        });
        // Indicador de fortaleza
        const pwNew = document.getElementById("profile-pw-new");
        const bar = document.getElementById("pw-strength-bar");
        const segs = ["pw-seg1", "pw-seg2", "pw-seg3"].map(id => document.getElementById(id));
        const lbl = document.getElementById("pw-strength-label");
        pwNew.addEventListener("input", () => {
          const pw = pwNew.value;
          if (!pw) { bar.style.display = "none"; return; }
          bar.style.display = "";
          let score = 0;
          if (pw.length >= 6) score++;
          if (pw.length >= 10) score++;
          if (/[A-Z]/.test(pw)) score++;
          if (/[0-9]/.test(pw)) score++;
          if (/[^A-Za-z0-9]/.test(pw)) score++;
          const lvl = score <= 1
            ? { n: 1, color: "#dc2626", text: "Muy débil" }
            : score === 2
              ? { n: 1, color: "#f97316", text: "Débil" }
              : score === 3
                ? { n: 2, color: "var(--color-accent-dark)", text: "Moderada" }
                : { n: 3, color: "var(--color-teal)", text: "Fuerte" };
          segs.forEach((seg, i) => { seg.style.background = i < lvl.n ? lvl.color : "#e0e0e0"; });
          lbl.textContent = lvl.text;
          lbl.style.color = lvl.color;
        });
      },
      preConfirm: async() => {
        const nombre = document.getElementById("profile-nombre").value.trim();
        const current = document.getElementById("profile-pw-current").value;
        const newPw = document.getElementById("profile-pw-new").value;
        const confirm = document.getElementById("profile-pw-confirm").value;

        if (!nombre) {
          Swal.showValidationMessage("El nombre es obligatorio");
          return false;
        }

        const cambiandoPassword = current || newPw || confirm;

        if (cambiandoPassword) {
          if (!current || !newPw || !confirm) {
            Swal.showValidationMessage("Para cambiar la contraseña completá los tres campos");
            return false;
          }
          if (newPw !== confirm) {
            Swal.showValidationMessage("Las contraseñas no coinciden");
            return false;
          }
          if (newPw.length < 6) {
            Swal.showValidationMessage("La nueva contraseña debe tener al menos 6 caracteres");
            return false;
          }
        }

        try {
          const res = await ApiService.post("auth/profile", { nombre, email: user.email });
          const updated = { ...AuthService.getCurrentUser(), nombre: res.nombre };
          localStorage.setItem("currentUser", JSON.stringify(updated));
        } catch (e) {
          Swal.showValidationMessage(e.message || "Error al guardar los datos");
          return false;
        }

        if (cambiandoPassword) {
          try {
            await ApiService.post("auth/password", { currentPassword: current, newPassword: newPw });
            return { tipo: "password" };
          } catch (e) {
            Swal.showValidationMessage(e.message || "Error al cambiar la contraseña");
            return false;
          }
        }

        return { tipo: "datos" };
      },
    });

    if (!value) return;

    if (value.tipo === "password") {
      await Swal.fire({ icon: "success", title: "Contraseña actualizada", text: "Vas a ser redirigido al login.", timer: 2000, showConfirmButton: false });
      AuthService.logout();
    } else {
      Swal.fire({ icon: "success", title: "Datos actualizados", timer: 1500, showConfirmButton: false });
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }
  },

  // Usuario de un proveedor externo (LCApii): nombre y clave son de ese proveedor
  openExterno: async(user) => {
    const etiqueta = escapeHtml(AppConfig.auth.label);
    await Swal.fire({
      title: "Mi perfil",
      html: `
        <div class="text-start">
          <div class="mb-3">
            <label class="form-label fw-semibold">Nombre</label>
            <input id="profile-nombre" class="form-control" value="${escapeHtml(user.nombre || "")}" disabled>
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Usuario</label>
            <input id="profile-usuario" class="form-control" value="${escapeHtml(user.usuario || "")}" disabled>
          </div>
          <p class="text-muted small mb-3">Tu nombre, usuario y clave son los de ${etiqueta}: se cambian ahí y se actualizan acá la próxima vez que ingreses.</p>
          <a id="profile-cambiar-clave" class="btn btn-outline-primary btn-sm" href="${AppConfig.auth.lcapiiUrl}/auth/chPass.html" target="_blank" rel="noopener">
            <i class="fas fa-key me-1"></i>Cambiar clave en ${etiqueta}
          </a>
        </div>
      `,
      confirmButtonText: "Cerrar",
      returnFocus: false,
    });
  },
};
